'use client';

import { useEffect, useRef } from 'react';

/**
 * Scroll-triggered fade + translateY reveal, matching
 * design_handoff_homepage_and_courts' `data-reveal` behavior: fires once per
 * element as it enters the viewport, disabled under prefers-reduced-motion.
 * Wrap whatever needs to reveal (a section header, a row, a single Link) —
 * this renders a plain block div, so it doesn't change surrounding layout.
 */
export default function Reveal({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced || !('IntersectionObserver' in window)) return;

    el.style.opacity = '0';
    el.style.transform = 'translateY(24px)';
    el.style.transition =
      'opacity 700ms cubic-bezier(0.25,1,0.5,1), transform 700ms cubic-bezier(0.25,1,0.5,1)';

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const target = entry.target as HTMLElement;
            target.style.opacity = '1';
            target.style.transform = 'translateY(0)';
            io.unobserve(target);
          }
        });
      },
      { threshold: 0.12 },
    );

    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div ref={ref} className={className}>
      {children}
    </div>
  );
}
